import { _ } from "../../../../core/polyglot/polyglot.js";
import { getCSSVariable } from "../../../../core/helpers/css-util.js";
import { getZeroPositionRatio } from "../../../../core/helpers/chart-util.js";
import AbstractChartStrategy from "../../../../core/widgets/ChartWidget/AbstractChartStrategy.js";

/**
 * Implement a Strategy to render a line chart for the forecast editor with periods on the
 * x-axis and amount of money on the y-axis. The beginning of the line
 * shows real accounting data with a straight line whereas the end of
 * the line shows forecast data with a dashed line.
 *
 * @param {number} indexOfFirstPredictedColumn - Index used to cut the provided
 *   values in two: referencesValues and predictedValues
 * @param {number} selectedPeriodIndex - Index of the currently selected period
 *   used to render a tooltip and to emphasize the corresponding line element
 */
export default class ForecastedValuesChartStrategy extends AbstractChartStrategy {
	constructor({ indexOfFirstPredictedColumn, selectedPeriodIndex }) {
		super(...arguments);
		this._indexOfFirstPredictedColumn = indexOfFirstPredictedColumn;
		this._selectedPeriodIndex = selectedPeriodIndex;
	}

	getConfiguration({ context, values }) {
		return {
			...super.getConfiguration({ context, values }),
			plugins: this._getPlugins(),
		};
	}

	getCssClass() {
		return "line-chart";
	}

	_makeDatasets({ values: rowValues }) {
		const referenceValues = rowValues.slice(
			0,
			this._indexOfFirstPredictedColumn,
		);
		const predictedValues = rowValues.slice(
			this._indexOfFirstPredictedColumn,
		);

		if (!referenceValues.length || !predictedValues.length) {
			return [];
		}

		this._zeroPositionRatio = getZeroPositionRatio({ data: rowValues });

		return [
			{
				data: this._makeData({
					referenceValues,
					predictedValues,
				}),
				radius: this._makeRadius({
					referenceValues,
					predictedValues,
				}),
				segment: {
					borderDash: this._makeBorderDash({ referenceValues }),
				},
			},
		];
	}

	_getChartType() {
		return "line";
	}

	_getPlugins() {
		return [
			{
				afterDatasetsUpdate: (chart) => {
					if (
						chart.tooltip.getActiveElements().length ||
						!chart.data.datasets.length
					) {
						return;
					}

					chart.tooltip.setActiveElements([
						{
							datasetIndex: 0,
							index: this._selectedPeriodIndex,
						},
					]);

					chart.update();
				},
			},
		];
	}

	_getChartOptions({ context }) {
		return {
			plugins: {
				legend: { display: false },
				tooltip: {
					intersect: false,
					callbacks: {
						label: this._makeChartItemLabel.bind(this),
					},
				},
			},
			borderColor: getCSSVariable("--line-chart-border-color"),
			borderWidth: 3,
			fill: {
				target: "origin",
				above: this._makePositiveGradient({ context }),
				below: this._makeNegativeGradient({ context }),
			},
			scales: {
				x: { display: false },
				y: {
					display: false,
					suggestedMin: 0,
					ticks: { suggestedMin: 0 },
				},
			},
		};
	}

	/**
	 * @return {object[]} An array of {x,y} coordinates, x being a
	 * period name while y is the accounting or forecast value at this
	 * period.
	 */
	_makeData({ referenceValues, predictedValues }) {
		return [
			...referenceValues.map((value, index) => ({
				x: this._labels[index],
				y: value,
				label: _("Actual"),
			})),
			...predictedValues.map((value, index) => ({
				x: this._labels[index + referenceValues.length],
				y: value,
				label: _("Predicted"),
			})),
		];
	}

	/**
	 * @return {number[]} An array of numbers, one for each period
	 * name, representing the size of the dot at this period in the
	 * line chart. All numbers are 0 except the one of the selected
	 * period.
	 */
	_makeRadius({ referenceValues, predictedValues }) {
		const radiusArray = [
			...new Array(
				Math.max(0, referenceValues.length + predictedValues.length),
			).fill(0),
		];

		radiusArray[this._selectedPeriodIndex] = getCSSVariable(
			"--line-chart-current-value-radius-in-px",
		);

		return radiusArray;
	}

	/**
	 * Return a specification for how the line between 2 points should
	 * be drawn. The specification is a straight line between two
	 * actual accounting values and a dashed line after that.
	 */
	_makeBorderDash({ referenceValues }) {
		const isForecastPointIndex = (pointIndex) =>
			pointIndex >= referenceValues.length;

		const isBetween2ActualValues = (context) =>
			!isForecastPointIndex(context.p1DataIndex);

		const noDashedLine = undefined;
		const dashedLine = [
			getCSSVariable("--line-chart-forecast-dash-line-length-in-px"),
			getCSSVariable("--line-chart-forecast-dash-gap-length-in-px"),
		];

		return (context) =>
			isBetween2ActualValues(context) ? noDashedLine : dashedLine;
	}

	_makeChartItemLabel(chartItem) {
		return `${chartItem.raw.label.toString()}: ${chartItem.formattedValue}`;
	}

	/**
	 * Return a gradient from blue to white to the chart nicely blends
	 * into its surroundings.
	 */
	_makeGradient({ context }) {
		const gradient = context.createLinearGradient(0, 0, 0, 200);

		gradient.addColorStop(
			0,
			getCSSVariable("--line-chart-gradient-stop-0"),
		);

		gradient.addColorStop(
			0.5,
			getCSSVariable("--line-chart-gradient-stop-1"),
		);

		gradient.addColorStop(
			1,
			getCSSVariable("--line-chart-gradient-stop-2"),
		);

		return gradient;
	}

	_getPositiveGradientColor() {
		return getCSSVariable("--line-chart-border-color");
	}

	_getNegativeGradientColor() {
		return this._getPositiveGradientColor();
	}

	_getPositiveEndPointForYAxis() {
		return getCSSVariable("--line-chart-positive-end-point");
	}

	_getNegativeEndPointForYAxis() {
		return getCSSVariable("--line-chart-negative-end-point");
	}
}
